import {useState} from "react";
import {useTranslation} from "react-i18next";
import {Typography} from "@interstate/components/Typography";
import {CheckCircleIcon} from "@interstate/components/Icons";
import {EDIT_ENTERPRISE_ENTITLEMENTS} from "../../../access";
import {OnCompleteCallback} from "../../../utils";
import {TabRow} from "../../../tab-content";
import {PermissionBasedEditButton} from "../../../action-buttons";
import {useOperationalEntity} from "../../context";
import {EditEnterpriseEntitlementsModal} from "./EditEnterpriseEntitlementsModal";
import "./EnterpriseEntitlements.scss";
import {isEntitlementActive} from "../../util/isEntitlementActive";

export const EnterpriseEntitlements = () => {
  const {t} = useTranslation();
  const [editing, setEditing] = useState<boolean>(false);
  const {
    entity: {id, enterpriseEntitlementsV2: entitlements}
  } = useOperationalEntity();

  const activeEntitlements =
    entitlements?.filter(entitlement => isEntitlementActive(entitlement)) ?? [];

  const finishEditing: OnCompleteCallback = () => {
    setEditing(false);
  };

  return (
    <TabRow
      name={"enterprise-entitlements"}
      header={
        <header
          id={`enterprise-entitlements-header-${id}`}
          data-testid={`enterprise-entitlements-header-${id}`}
          className={"details-column-header"}>
          {editing && (
            <EditEnterpriseEntitlementsModal
              id={id}
              onComplete={finishEditing}
              initialEntitlements={entitlements || []}
            />
          )}
          <hgroup className={"details-column-header-group"}>
            <h4>{t("entity-detail.dealer.enterprise-entitlements.details")}</h4>
            <PermissionBasedEditButton
              onClick={() => setEditing(true)}
              qualifier={"enterprise-entitlements"}
              tooltip={t("entity-detail.dealer.enterprise-entitlements.edit")}
              permissions={[EDIT_ENTERPRISE_ENTITLEMENTS]}
            />
          </hgroup>
        </header>
      }>
      {activeEntitlements.length > 0 ? (
        activeEntitlements.map(entitlement => (
          /*The key/id is the featureId, specific to the feature
            eg: connected customer - name mapped to feature Id of abc123xyz,
            the id is a unique id to each record, each instance of an entitlement.
            Feature Ids should be unique per page, eg, there will only ever be one tied to an entity,
            */
          <div
            key={entitlement.featureId}
            id={`enterprise-entitlement-${entitlement.featureId}`}
            data-testid={`enterprise-entitlement-${entitlement.featureId}`}
            className={"enterprise-entitlement-item"}>
            <CheckCircleIcon className={"enterprise-entitlement-item-icon"} />
            {entitlement.featureName}
          </div>
        ))
      ) : (
        <Typography
          tag={"span"}
          className={"no-enterprise-entitlements"}
          variant={"body-sm"}>
          {t("entity-detail.dealer.enterprise-entitlements.none")}
        </Typography>
      )}
    </TabRow>
  );
};
